import { ChevronRight, MapPin } from 'lucide-react';
import styled, { css } from 'styled-components';
import { key, palette } from 'styled-theme';

export const ApplicationCardWrapper = styled.article`
  background-color: white;
  border-radius: 4px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  padding: 24px 24px 16px;
  cursor: pointer;

  &:focus-within {
    outline: 2px solid #7b5cff;
    outline-offset: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #7b5cff;
  }
`;

export const ProcedureName = styled.h2`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

export const ProcedureId = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 0.875rem;
  color: #6b7280;
  display: block;
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AddressLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

export const AddressText = styled.address`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1rem;
  font-style: normal;
  color: #4b5563;
  line-height: 1.5;
`;

export const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DescriptionLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1rem;
  color: #1f2937;
  line-height: 1.5;
  margin: 0;
`;

export const DescriptionText = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 0.25rem;
`;

export const Main = styled.main`
  margin-top: 95px;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

export const ApplicationContainer = styled.div`
  width: 84%;
  margin: auto;
`;

export const Container = styled.div`
  padding: 0.5rem 1rem;
`;

export const BackButton = styled.button`
  cursor: pointer;
  margin-bottom: 1rem;
  background: transparent;
  color: #2563eb;
  border: none;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  padding: 4px 5px 4px 0px;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;

  &:hover {
    color: #1d4ed8;
  }
`;

export const Card = styled.article`
  background-color: white;
  border-radius: 5px;
  padding: 2rem;
  border: 1px solid ${palette('border', 1)};
`;

export const Header = styled.header`
  margin-bottom: 2rem;
  border-bottom: 1px solid ${palette('border', 0)};
  padding-bottom: 1.5rem;
`;

export const Title = styled.h1`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

export const Section = styled.section`
  margin-bottom: 1.5rem;
`;

export const SectionTitle = styled.h2`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1.125rem;
  font-weight: 500;
  color: #4b5563;
  margin: 0 0 1rem 0;
`;

export const DetailItem = styled.div`
  margin-bottom: 1.5rem;
`;

export const DetailLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  display: block;
  margin-bottom: 0.25rem;
`;

export const DetailValue = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 1rem;
  color: #1f2937;
  margin: 0;
  line-height: 1.5;
`;

export const Address = styled(DetailValue)`
  font-style: normal;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.status === 'approved'
      ? '#10B981'
      : props.status === 'pending'
        ? '#F59E0B'
        : props.status === 'rejected'
          ? '#EF4444'
          : '#6B7280'};
  color: white;
  margin-left: 1rem;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

export const ErrorContainer = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 1rem;
  margin: 2rem 0;
  color: #b91c1c;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const TagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ProjectTypeTag = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0369a1;
  background-color: #e0f2fe;
  padding: 4px 12px;
  border-radius: 4px;
`;

export const ApplicationTypeTag = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.color || '#2E7D32'};
  background-color: ${(props) => props.backgroundColor || '#E8F5E9'};
  padding: 4px 12px;
  border-radius: 4px;
`;

export const ApplicationId = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const AddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-weight: 500;
  font-size: 1.125rem;
  color: #1f2937;
`;

export const LocationIcon = styled(MapPin)`
  color: #9ca3af;
  margin-right: 8px;
  flex-shrink: 0;
  margin-top: 3px;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

export const SubmittedDate = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const ViewDetailsButton = styled.button`
  background: transparent;
  color: #2563eb;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  padding: 0;

  &:hover {
    color: #1d4ed8;
  }
`;

export const ChevronIcon = styled(ChevronRight)`
  margin-left: 2px;
`;

export const EmptyState = styled.div`
  padding: 20px 0;
  text-align: center;
`;

export const FlexLayout = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  grid-auto-flow: column;
  gap: 20px;

  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

export const OverlaysCard = styled.div`
  background: #fff;
  border-radius: 4px;
  padding: 1.5rem;
  height: fit-content;
  flex: 1;
`;

export const OverlayList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const OverlayItem = styled.li`
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

export const OverlayName = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

export const ViewDocumentLink = styled.button`
  background: #e6f7ff;
  color: #1890ff;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  gap: 5px;
  font-family: 'JetBrains Mono Regular';

  &:hover {
    background: #bae7ff;
  }
`;

export const StatusTag = styled.span`
  background: #f5f5f5;
  color: #666;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
`;

export const ReportItem = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props) =>
    props.isLast &&
    css`
      border-bottom: none;
      margin-bottom: 6px;
    `}
`;

export const UploadButton = styled.a`
  background: #e8f9d8;
  color: #52c41a;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;

  &:hover {
    background: #d9f7be;
  }
`;

export const WelcomeContainer = styled.div`
  padding: 1.3rem 0;
  margin-bottom: 1rem;
`;

export const WelcomeHeading = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #1f1f1f;
  margin: 0;
  padding: 0;
  margin-bottom: 0.5rem;
`;

export const WelcomeSubtext = styled.p`
  font-size: 1.25rem;
  color: #555;
  margin: 0;
  padding: 0;
`;

export const TooltipWrapper = styled.div`
  position: relative;
`;

export const TooltipContent = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};
  background-color: #f8f9fa;
  color: #2c3e50;
  text-align: center;
  padding: 10px 14px;
  border-radius: 8px;
  position: fixed;
  z-index: 9999;
  line-height: 1.5;
  font-size: 13px;
  pointer-events: none;
  min-width: 300px;
  max-width: 300px;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border: 1px solid #7a61ff;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -7px;
    border-width: 7px;
    border-style: solid;
    border-color: #f8f9fa transparent transparent transparent;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -8px;
    border-width: 8px;
    border-style: solid;
    border-color: #7a61ff transparent transparent transparent;
    z-index: -1;
  }
`;

export const DisabledOverlay = styled.div`
  position: relative;
  opacity: 0.5;
  cursor: not-allowed;

  button {
    cursor: not-allowed;
    pointer-events: auto !important;
  }
`;

export const ConvertButton = styled.button`
  display: flex;
  align-items: center;
  justify-self: flex-end;
  gap: 8px;
  padding: 10px 16px;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.3s,
    transform 0.1s;
  margin-top: 12px;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform 0.3s;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;
